import { Injectable } from "@angular/core";
import { AngularFire } from "angularfire2";
import { UserService } from "./user";
import { StatsService } from "./stats";
import { each, camelCase } from "lodash";

@Injectable()
export class PostService {
  private static readonly ACTIVITY_RESOLVED = "resolved";
  private static readonly ACTIVITY_OPEN = "open";

  private static readonly BLOCK_STATE_BLOCKED = "hasbeenblocked";
  private static readonly BLOCK_STATE_NOT_BLOCKED = "hasnotbeenblocked";

  private static readonly MODERATED_STATE_MODERATED = "hasbeenmoderated";
  private static readonly MODERATED_STATE_NOT_MODERATED = "hasnotbeenmoderated";

  private static readonly READ_STATE_READ = "hasbeenread";
  private static readonly READ_STATE_NOT_READ = "hasnotbeenread";

  private static readonly TYPE_ARTICLE = "article";
  private static readonly TYPE_POST = "post";

  constructor(
    public af: AngularFire,
    public userService: UserService,
    public statsService: StatsService,
  ) {}

  updateBlockedStatus(blocked: boolean, post: any) {
    post.blocked = blocked
      ? PostService.postBlockStateBlocked
      : PostService.postBlockStateNotBlocked;

    return this.updatePostMeta(post)
      .then(() => {
        return Promise.all([
          this.statsService.trackTotalNumberOfBlockedPosts(post),
          this.statsService.trackRankedListOfBlockedPosts(post),
        ]);
      })
      .then(() => post);
  }

  updateReadStatus(read: boolean, post: any) {
    post.markedAsRead = read
      ? PostService.postReadStateRead
      : PostService.postReadStateNotRead;

    return this.updatePostMeta(post);
  }

  updateModeratedStatus(moderated: boolean, post: any) {
    post.moderated = moderated
      ? PostService.postModerateStateModerated
      : PostService.postModerateStateNotModerated;

    return this.updatePostMeta(post);
  }

  // CRUD
  deletePost(post: any): Promise<boolean> {
    let deletionPosts = [];

    [
      `posts/${post.$key}`,
      `private/${post.$key}`,
      `reported/${post.$key}`,
      `content/${post.userId}/${post.$key}`,
      `favourites/${post.userId}/${post.$key}`,
    ].map(ref => {
      deletionPosts.push(this.af.database.object(ref).remove());
    });

    this.userService.getUserSafeContacts(post.userId).then(contacts => {
      let deleteInteractions = [];

      each(contacts, (contact, key) => {
        if (!contact.email) {
          return;
        }

        deleteInteractions.push(
          this.af.database
            .object(`interactions/${camelCase(contact.email)}/${post.$key}`)
            .remove(),
        );
      });

      return Promise.all(deleteInteractions);
    });

    return Promise.all(deletionPosts)
      .then(() => {
        return this.statsService.trackTotalNumberOfDeletedPosts();
      })
      .then(() => true)
      .catch(() => false);
  }

  updatePostMeta(post: any, user: any = null): Promise<any> {
    return Promise.all([
      this.updatePostByRef(`posts/${post.$key}`, post),
      this.updatePostByRef(`private/${post.$key}`, post),
      this.updatePostByRef(`reported/${post.$key}`, post),
      this.updatePostByRef(`content/${post.userId}/${post.$key}`, post),
      this.updatePostByRef(`favourites/${post.userId}/${post.$key}`, post),
      this.userService.getUserSafeContacts(post.userId).then(contacts => {
        // console.log("Contacts", contacts);
        let interactions = [];

        each(contacts, (contact, key) => {
          if (!contact.email) {
            return;
          }

          interactions.push(
            this.updatePostByRef(
              `interactions/${camelCase(contact.email)}/${post.$key}`,
              post,
            ),
          );
        });

        return Promise.all(interactions);
      }),
    ]).then(() => post);
  }

  updatePostByRef(ref: string, post: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.af.database.object(ref).take(1).subscribe(data => {
        if (!data.$exists()) {
          return resolve(post);
        }

        return this.af.database
          .object(ref)
          .update(post)
          .then((post: any) => {
            return resolve(post);
          })
          .catch(error => {
            return reject(error);
          });
      });
    });
  }

  // Getters and setters
  static get postActivityResolved() {
    return PostService.ACTIVITY_RESOLVED;
  }

  static get postActivityOpen() {
    return PostService.ACTIVITY_OPEN;
  }

  static get postBlockStateBlocked() {
    return PostService.BLOCK_STATE_BLOCKED;
  }

  static get postBlockStateNotBlocked() {
    return PostService.BLOCK_STATE_NOT_BLOCKED;
  }

  static get postModerateStateModerated() {
    return PostService.MODERATED_STATE_MODERATED;
  }

  static get postModerateStateNotModerated() {
    return PostService.MODERATED_STATE_NOT_MODERATED;
  }

  static get postReadStateRead() {
    return PostService.READ_STATE_READ;
  }

  static get postReadStateNotRead() {
    return PostService.READ_STATE_NOT_READ;
  }

  static get postTypeArticle() {
    return PostService.TYPE_ARTICLE;
  }

  static get postTypePost() {
    return PostService.TYPE_POST;
  }
}



// WEBPACK FOOTER //
// ./src/providers/post.ts